import { writeBundle, type BundleInput, type FetchMedia } from "@goalhorns/bundle";
import { ApiError, apiRequest } from "../../lib/api";
import type { Person, Slot } from "../../lib/api-types";
import { paths } from "../../lib/endpoints";

export type ExportPhase = "preparing" | "writing" | "marking" | "done";

export interface ExportProgress {
  phase: ExportPhase;
  done: number;
  total: number;
}

/** Where the zip goes. Implementations: a file picked by the user, or a Blob download. */
export interface SaveSink {
  write(stream: ReadableStream<Uint8Array>): Promise<void>;
}

/** The export-input cells carry sha256 refs only; the API needs media ids. */
type MediaIdIndex = ReadonlyMap<string, string>;

/**
 * Maps each sha256 in the bundle to the media id that serves it. A cell's position matches its
 * slot's position, and the slot's person owns the photo and song ids.
 */
export function buildMediaIdIndex(
  input: BundleInput,
  slots: readonly Slot[],
  persons: readonly Person[],
): MediaIdIndex {
  const personById = new Map(persons.map((person) => [person.id, person]));
  const personByPosition = new Map<number, Person>();
  for (const slot of slots) {
    const person = personById.get(slot.personId);
    if (person) personByPosition.set(slot.position, person);
  }
  const index = new Map<string, string>();
  for (const cell of input.cells) {
    const person = personByPosition.get(cell.position);
    if (!person) throw new ApiError(409, "export_invalid", "A slot has no person");
    if (cell.photo) index.set(cell.photo.sha256, requireId(person.photoMediaId));
    if (cell.song) index.set(cell.song.sha256, requireId(person.songMediaId));
  }
  return index;
}

function requireId(mediaId: string | null): string {
  if (mediaId === null) throw new ApiError(409, "export_invalid", "A media file is missing");
  return mediaId;
}

export function countMediaRefs(input: BundleInput): number {
  return input.cells.reduce((sum, cell) => sum + (cell.photo ? 1 : 0) + (cell.song ? 1 : 0), 0);
}

/** Streams one media file from the API, looked up through the sha256 index. */
export function mediaFetcher(
  workspaceId: string,
  index: MediaIdIndex,
  onFetched: () => void,
): FetchMedia {
  return async (sha256) => {
    const mediaId = index.get(sha256);
    if (!mediaId) throw new ApiError(409, "export_invalid", "Unknown media reference");
    const response = await fetch(paths.mediaFile(workspaceId, mediaId), { credentials: "include" });
    if (!response.ok || response.body === null) {
      throw new ApiError(response.status, "http_error", "A media file could not be downloaded");
    }
    onFetched();
    return response.body;
  };
}

export interface ExportOptions {
  workspaceId: string;
  configurationId: string;
  sink: SaveSink;
  onProgress: (progress: ExportProgress) => void;
}

/**
 * Builds the .goalhorns zip in the browser and writes it to the sink, then records the
 * download. The download is recorded only after the write has finished.
 */
export async function exportConfiguration(options: ExportOptions): Promise<void> {
  const { workspaceId, configurationId, sink, onProgress } = options;
  onProgress({ phase: "preparing", done: 0, total: 0 });
  const input = await apiRequest<BundleInput>(paths.exportInput(workspaceId, configurationId));
  const [slots, persons] = await Promise.all([
    apiRequest<Slot[]>(paths.slots(workspaceId, configurationId)),
    apiRequest<Person[]>(paths.persons(workspaceId)),
  ]);
  const index = buildMediaIdIndex(input, slots, persons);
  const total = countMediaRefs(input);
  let done = 0;
  const fetchMedia = mediaFetcher(workspaceId, index, () => {
    done += 1;
    onProgress({ phase: "writing", done, total });
  });
  await sink.write(writeBundle(input, fetchMedia));
  onProgress({ phase: "marking", done, total });
  await apiRequest<{ lastDownloadedAt: string }>(
    paths.markDownloaded(workspaceId, configurationId),
    {
      method: "POST",
    },
  );
  onProgress({ phase: "done", done, total });
}
